import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { setLanguage } from '../store/slices/appSlice'
import { getLanguageOption, languageOptions } from '../i18n/languageOptions'

function LanguageFlag({ code, size = 'sm' }) {
  const className = `${
    size === 'lg' ? 'h-[14px] w-[20px]' : 'h-[10px] w-[14px]'
  } shrink-0 overflow-hidden rounded-[2px] shadow-[0_0_0_1px_rgba(255,255,255,0.42)]`

  if (code === 'zh-CN') {
    return (
      <span className={className} aria-hidden="true">
        <svg viewBox="0 0 28 20" className="h-full w-full" focusable="false">
          <rect width="28" height="20" fill="#de2910" />
          <polygon points="5,3 5.8,5.4 8.4,5.4 6.3,6.9 7.1,9.3 5,7.8 2.9,9.3 3.7,6.9 1.6,5.4 4.2,5.4" fill="#ffde00" />
          <polygon points="10.7,2.2 11.2,3.5 12.6,3.6 11.5,4.4 11.8,5.8 10.7,5 9.6,5.8 10,4.4 8.8,3.6 10.2,3.5" fill="#ffde00" />
          <polygon points="13,5 13.4,6.3 14.8,6.3 13.7,7.2 14,8.5 13,7.7 11.9,8.5 12.3,7.2 11.2,6.3 12.6,6.3" fill="#ffde00" />
          <polygon points="13,9.1 13.4,10.4 14.8,10.5 13.7,11.3 14,12.7 13,11.9 11.9,12.7 12.3,11.3 11.2,10.5 12.6,10.4" fill="#ffde00" />
          <polygon points="10.7,12.3 11.2,13.6 12.6,13.7 11.5,14.5 11.8,15.9 10.7,15.1 9.6,15.9 10,14.5 8.8,13.7 10.2,13.6" fill="#ffde00" />
        </svg>
      </span>
    )
  }

  if (code === 'en-US') {
    const stripes = Array.from({ length: 13 }, (_, index) => (
      <rect
        key={index}
        y={index * (20 / 13)}
        width="28"
        height={20 / 13}
        fill={index % 2 === 0 ? '#b22234' : '#fff'}
      />
    ))

    return (
      <span className={className} aria-hidden="true">
        <svg viewBox="0 0 28 20" className="h-full w-full" focusable="false">
          {stripes}
          <rect width="11.2" height="10.8" fill="#3c3b6e" />
          {Array.from({ length: 12 }, (_, index) => (
            <circle
              key={index}
              cx={1.6 + (index % 4) * 2.4}
              cy={1.5 + Math.floor(index / 4) * 2.6}
              r="0.45"
              fill="#fff"
            />
          ))}
        </svg>
      </span>
    )
  }

  if (code === 'ja-JP') {
    return (
      <span className={className} aria-hidden="true">
        <svg viewBox="0 0 28 20" className="h-full w-full" focusable="false">
          <rect width="28" height="20" fill="#fff" />
          <circle cx="14" cy="10" r="5.2" fill="#bc002d" />
        </svg>
      </span>
    )
  }

  return (
    <span className={className} aria-hidden="true">
      <svg viewBox="0 0 28 20" className="h-full w-full" focusable="false">
        <rect width="28" height="20" fill="#fff" />
        <path d="M14 4.2a5.8 5.8 0 0 1 0 11.6 2.9 2.9 0 0 0 0-5.8 2.9 2.9 0 0 1 0-5.8Z" fill="#0047a0" />
        <path d="M14 4.2a2.9 2.9 0 0 1 0 5.8 2.9 2.9 0 0 0 0 5.8 5.8 5.8 0 0 1 0-11.6Z" fill="#cd2e3a" />
        <g stroke="#111" strokeWidth="0.8">
          <path d="M5.4 4.2 8.4 2.2M6.1 5.2 9.1 3.2M6.8 6.2 9.8 4.2" />
          <path d="M19.6 15.8 22.6 13.8M20.3 16.8 23.3 14.8M21 17.8 24 15.8" />
          <path d="M22.6 4.2 19.6 2.2M23.3 3.2 20.3 1.2M24 2.2 21 0.2" />
          <path d="M8.4 15.8 5.4 13.8M9.1 16.8 6.1 14.8M9.8 17.8 6.8 15.8" />
        </g>
      </svg>
    </span>
  )
}

function LanguageSwitch({ className = '' }) {
  const { i18n, t } = useTranslation()
  const dispatch = useDispatch()
  const { language } = useSelector((state) => state.app)
  const currentLanguage = i18n.resolvedLanguage || i18n.language || language
  const activeOption = getLanguageOption(currentLanguage) || languageOptions[0]
  const activeLabel = t(`languageNames.${activeOption.code}`, { defaultValue: activeOption.label })
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [])

  const handleSelectLanguage = (nextLanguage) => {
    if (nextLanguage === currentLanguage) {
      setIsOpen(false)
      return
    }

    i18n.changeLanguage(nextLanguage)
    dispatch(setLanguage(nextLanguage))
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t('languageSwitch.buttonLabel')}
        className={`font-header-nav inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] transition-colors duration-300 md:text-[12px] ${
          isOpen ? 'text-white' : 'text-white hover:text-white/92'
        }`}
      >
        <LanguageFlag code={activeOption.code} />
        <span className="font-language-switch leading-none">
          {/^[A-Za-z ]+$/.test(activeLabel) ? activeLabel.toUpperCase() : activeLabel}
        </span>
        <span className={`material-symbols-outlined text-[16px] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          expand_more
        </span>
      </button>

      <div
        role="menu"
        aria-label={t('languageSwitch.menuLabel')}
        className={`absolute right-0 top-full z-50 mt-4 w-[200px] overflow-hidden border border-[#b9afa3] bg-[rgba(187,176,164,0.98)] py-6 text-white shadow-[0_22px_48px_rgba(0,0,0,0.34)] transition-all duration-200 ${
          isOpen ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-1 opacity-0'
        }`}
      >
        <div className="flex flex-col gap-2">
          {languageOptions.map((option) => {
            const isActive = option.code === currentLanguage
            const optionLabel = t(`languageNames.${option.code}`, { defaultValue: option.label })

            return (
              <button
                key={option.code}
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => handleSelectLanguage(option.code)}
                className={`flex w-full items-center gap-4 px-8 py-3 text-left transition-colors duration-200 ${
                  isActive ? 'bg-black/6' : 'hover:bg-black/8'
                }`}
              >
                <LanguageFlag code={option.code} size="lg" />
                <span className="font-header-nav text-[17px] font-normal leading-none tracking-[0.01em] text-white">
                  {optionLabel}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default LanguageSwitch
